// Conference Room Chat panel — real Square Code Communications
// (sq_communication_channels / sq_messages) via useChannelMessages, reused
// verbatim from the Communications lane. Renders only when this call was
// started from (or explicitly linked to) a real channel; otherwise the
// caller keeps showing the honest blocked HonestPanel — this component is
// never mounted without a channelId.

import { useState } from 'react'
import { useChannelMessages } from '../commsData'
import { initialsOf } from './conferenceRoomModel'

const MONO = 'var(--sc-font-mono, "JetBrains Mono", ui-monospace, monospace)'

export function ChatPanel({ channelId }: { channelId: string }) {
  const msgs = useChannelMessages(channelId)
  const [draft, setDraft] = useState('')
  const [sending, setSending] = useState(false)

  const send = async () => {
    const body = draft.trim()
    if (!body || sending) return
    setSending(true)
    try {
      await msgs.send(body)
      setDraft('')
    } finally {
      setSending(false)
    }
  }

  if (msgs.authRequired) {
    return <p className="cr-state-note">Sign in to read this channel's messages.</p>
  }
  if (msgs.error) {
    return <p className="cr-state-note">{msgs.error}</p>
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <div style={{ fontFamily: MONO, fontSize: 10, color: '#8a93a3', marginBottom: 8 }}>LIVE · Square Code Communications</div>
      <div style={{ flex: 1, overflowY: 'auto', display: 'grid', gap: 8, alignContent: 'start' }}>
        {msgs.loading && <p className="cr-state-note">Loading messages…</p>}
        {!msgs.loading && msgs.messages.length === 0 && <p className="cr-state-note">No messages yet — say hello.</p>}
        {msgs.messages.map((m) => (
          <div key={m.id} style={{ display: 'flex', gap: 8 }}>
            <span style={{ width: 24, height: 24, borderRadius: '50%', display: 'grid', placeItems: 'center', fontFamily: MONO, fontWeight: 800, fontSize: 9.5, color: '#cbd3df', background: 'linear-gradient(150deg, rgba(8,145,178,0.35), rgba(37,99,235,0.25))', flex: '0 0 auto' }}>{initialsOf(m.sender_id)}</span>
            <div style={{ fontSize: 12.5, color: '#e8ecf2', lineHeight: 1.5 }}>{m.body}</div>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') void send() }}
          placeholder="Message the channel…"
          aria-label="Message this channel"
          style={{ flex: 1, padding: '8px 10px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.14)', background: 'rgba(255,255,255,0.03)', color: '#e8ecf2', fontSize: 12.5 }}
        />
        <button type="button" onClick={() => void send()} disabled={sending || !draft.trim()}>Send</button>
      </div>
    </div>
  )
}
